import { io } from 'socket.io-client';
import { SOCKET_BASE, AUTH_TOKEN } from '../config';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { useCutscene } from '../city/cutscene';
import { bindVenueMingle } from '../city/venueMingle';
import { ROOMS } from '../rooms';
import { useBodyStore } from '../game/mannequins';
import { useHeliTour } from '../city/heliTour';

// worldSocket — the client half of the /world namespace (see API src/sockets/world.js).
// Two jobs mirroring the server: (1) keep a live roster of PEERS in your current
// area and feed them into worldStore.presences; (2) track a server-time OFFSET so
// serverNow() agrees with everyone else (shared clock). Position emitting (emitMove)
// is called from the frame loop once wired; until then peers show without live pos.

let socket = null;
let offset = 0;                 // serverNow() = Date.now() + offset
const peers = new Map();        // socket.id -> { id, handle, avatarUrl, member, pos, heading }
let storeUnsub = null;          // worldStore subscription (cleaned up on disconnect)
let bodyUnsub = null;           // the mannequin choice, told to the room when it changes
const connectListeners = new Set(); // onWorldConnect: an open city chat re-engages after a drop

// Shared world clock. Corrected off each `world_state {t}` broadcast. RTT is ignored
// (a few hundred ms is irrelevant for day/night + beat).
export function serverNow() { return Date.now() + offset; }
if (import.meta.env.DEV && typeof window !== 'undefined') window.__serverNow = serverNow;   // probes only

// Which of a MEMBER's own artists the story is about (avatar_id). Sent in the socket
// handshake; the server answers with `me`. ⚠ NOT who you appear as — nobody walks
// as an artist any more (Khari, 2026-10-23: *"the artists will live their own
// lives"*). Every visitor is a figure under their own name. The key keeps its old
// name so a remembered pick still reaches the server.
const APPEAR_KEY = 'world:appearAs';
export function getAppearAs() { try { return localStorage.getItem(APPEAR_KEY) || ''; } catch { return ''; } }
export function setAppearAs(avatarId) {
  try { localStorage.setItem(APPEAR_KEY, String(avatarId || '')); } catch { /* private mode */ }
  disconnectWorld();
  connectWorld();
}

function pushPeers() {
  // `userId` mirrors `id` so the existing venue presence render (keys on userId) keeps
  // working while we transition to position-accurate rendering.
  const list = [...peers.values()].map((p) => ({ ...p, userId: p.id }));
  useWorldStore.getState().setPresences(list);
}

// The area you're in — an interior id when inside, else the coarse zoom (street /
// district / orbit). People sharing an area exchange positions.
function roomKey() {
  const s = useWorldStore.getState();
  return s.interior || (s.zoom || 'street');
}

// ── SHARED SEATS (API sockets/sharedSeatStore, 2026-10-30) ──────────────────
// Khari: "This indoor room is a shared live experience (like the plaza), so
// seats will actually be limited. Prevent sitting at one if it is taken, and
// limit the time a user can sit (2 min)." Rooms flagged `sharedSeats` only —
// a listening room's chairs (`lr:` keys) keep their own server path.
//
// THE SAME TWO MINUTES as the server's hold, for when there is no server to
// ask (a dropped socket, an API without the seat handlers): you still sit, on
// this browser's clock, and are still stood up when it runs out.
export const SEAT_HOLD_MS = 2 * 60 * 1000;

// The server's `until` on THIS browser's clock. `t` is the server time at send,
// so the conversion does not wait on a `world_state` tick to have landed.
function toLocal(until, t) {
  const u = Number(until);
  if (!Number.isFinite(u)) return Date.now() + SEAT_HOLD_MS;
  return Number.isFinite(Number(t)) ? Date.now() + (u - Number(t)) : u - offset;
}

/**
 * Ask for a chair before sitting in it. Resolves
 *   { ok: true, until }               held for you (until = local ms)
 *   { ok: true, offline: true, until } nobody to ask: sit locally, as before
 *   { ok: false, reason: 'taken', by } somebody is in it
 *   { ok: false, reason: 'slow_down' } a second tap on the way: ignore it
 * LIVE-SAFE like cityEngage: no answer in 2.5s (an API that predates the seat
 * handlers) is the offline case, never a chair you can't sit in.
 */
export function seatTake(zone, seat, group = null) {
  return new Promise((resolve) => {
    const local = () => resolve({ ok: true, offline: true, until: Date.now() + SEAT_HOLD_MS });
    if (!socket?.connected || !zone || !seat) { local(); return; }
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; local(); } }, 2500);
    // `group`: the table this chair is at (rooms.js `tables`), so a table of two
    // gets the longer hold and talks privately (API sharedSeatStore).
    socket.emit('seat_take', { zone, seat, ...(group ? { group } : {}) }, (r) => {
      if (done) return;
      done = true; clearTimeout(t);
      if (r?.ok) resolve({ ok: true, until: toLocal(r.until, r.t) });
      else if (r?.reason === 'taken') resolve({ ok: false, reason: 'taken', by: r.by || null });
      else if (r?.reason === 'slow_down') resolve({ ok: false, reason: 'slow_down' });
      // Not a chair the server will hold (bad_input / not_here): sit as before
      // rather than strand the tap.
      else local();
    });
  });
}
export function seatLeave(zone, seat) {
  if (socket?.connected && zone && seat) socket.emit('seat_leave', { zone, seat });
}
export function seatHello(zone) {
  if (socket?.connected && zone) socket.emit('seat_hello', { zone });
}

/**
 * STILL HERE (2026-11-01, Hype-U). Khari: "When the lesson ended, I was ejected
 * from my seat. Allow for a question or short chat after. Eject after a period
 * of inactivity." In a room whose chairs are kept while you are busy in them
 * (rooms.js `seatKeepAlive` — a classroom), the class says so as it runs and as
 * you ask: the server's hold becomes two minutes from NOW (API sharedSeatStore
 * keepActive, capped), so the two minutes count from the last thing you did.
 * Resolves { ok: true, until } (local ms), or { ok: false, reason }.
 * LIVE-SAFE: no answer in 2.5s (an API without the handler) holds it on this
 * browser's clock, as an unanswered take does.
 */
// ── HELI TOURS (API sockets/heliTourStore, 2026-11-01) ───────────────────────
// Four pads, five seats a helicopter, the first aboard picks the flight and
// takes off; the server stamps `departAt` and everyone flies the same track on
// its clock (city/heliTour). `heli_state` is every pad, whole, to everyone in
// the street. Times arrive on the server's clock and are kept on this one.
function heliPadsLocal(pads, t) {
  const out = {};
  for (const [id, p] of Object.entries(pads || {})) {
    out[id] = { ...p, departAt: p.departAt ? toLocal(p.departAt, t) : null, endsAt: p.endsAt ? toLocal(p.endsAt, t) : null, seats: Array.isArray(p.seats) ? p.seats : [] };
  }
  return out;
}
const heliAsk = (event, payload) => new Promise((resolve) => {
  if (!socket?.connected) { resolve({ ok: false, offline: true }); return; }
  let done = false;
  const t = setTimeout(() => { if (!done) { done = true; resolve({ ok: false, offline: true }); } }, 2500);
  socket.emit(event, payload, (r) => {
    if (done) return;
    done = true; clearTimeout(t);
    if (r?.pads) useHeliTour.setState({ pads: heliPadsLocal(r.pads, r.t), offline: false, myId: socket?.id || null });
    resolve(r || { ok: false });
  });
});
/** Board a pad's helicopter: { ok, seat } | { ok: false, reason: 'full' | 'flying' | 'not_here' | … } | { offline: true } (no answer: an API without heli tours). */
export const heliBoard = (pad) => heliAsk('heli_board', { pad });
/** Get out (only before it lifts off). */
export const heliLeave = (pad) => heliAsk('heli_leave', { pad });
/** The first aboard picks the flight… */
export const heliPick = (pad, flight) => heliAsk('heli_pick', { pad, flight });
/** …and takes off: the server stamps `departAt` a few seconds out. */
export const heliDepart = (pad, flight) => heliAsk('heli_depart', { pad, flight });

export function seatActive(zone, seat) {
  return new Promise((resolve) => {
    const local = () => resolve({ ok: true, offline: true, until: Date.now() + SEAT_HOLD_MS });
    if (!socket?.connected || !zone || !seat) { local(); return; }
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; local(); } }, 2500);
    socket.emit('seat_active', { zone, seat }, (r) => {
      if (done) return;
      done = true; clearTimeout(t);
      if (r?.ok) resolve({ ok: true, until: toLocal(r.until, r.t) });
      else resolve({ ok: false, reason: r?.reason || 'refused' });
    });
  });
}

// OUT OF THE CHAIR, BY ANY ROUTE, LETS IT GO — the chair's Stand up, Done, the
// two minutes running out, world.stand(), leaving the room. Hung off the state
// rather than any button, so there is one way to free a chair and no way to
// walk off still holding it.
function releaseOnStand(s, prev) {
  const was = prev?.seated;
  const now = s.seated;
  if (!was || was === now || !was.held || was.phase === 'standing') return;
  if (now && now.phase !== 'standing' && now.target === was.target) return;
  seatLeave(was.zone, was.target);
}

// A DROPPED CONNECTION LET GO OF YOUR CHAIR (the server frees a seat when its
// socket goes). Still in it when the socket is back — or sat down while it was
// away — so ask for it now: held again if it is free, and stood up if somebody
// took it in the gap. Your two minutes stay the ones you started with.
function reclaimSeat() {
  const s = useWorldStore.getState().seated;
  if (!s?.zone || !s.until || s.phase === 'standing' || s.zone !== roomKey()) return;
  seatTake(s.zone, s.target).then((r) => {
    const st = useWorldStore.getState();
    const still = st.seated && st.seated.target === s.target && st.seated.phase !== 'standing';
    if (!still) { if (r.ok && !r.offline) seatLeave(s.zone, s.target); return; }
    if (r.ok && !r.offline && !st.seated.held) {
      useWorldStore.setState((x) => (x.seated ? { seated: { ...x.seated, held: true } } : x));
    } else if (!r.ok && r.reason === 'taken') {
      st.standUp();
      st.setSeatNote('Someone else has that seat now');
    }
  });
}

export function connectWorld() {
  if (socket) return socket;
  const wanted = getAppearAs() || undefined;
  // WHO WE ASKED TO BE vs WHO WE CAME BACK AS. The server falls back to your
  // first owned avatar when it cannot honour `appearAs`, and it did so silently
  // — so "I picked a new artist and entered as my old one" (Khari, 2026-08-06)
  // is indistinguishable from the client never having sent the pick. These two
  // lines split that in half without a restart.
  console.info(`[world] connecting as appearAs=${wanted ?? 'NONE (server will pick your first avatar)'}`);
  socket = io(`${SOCKET_BASE}/world`, {
    // `body`: the mannequin you walk in (game/mannequins), so others see it
    // (API sockets/world.js peerOf). Changes after this go out as `body` below.
    // A FUNCTION, read on every (re)connect, so a reconnect after a change of
    // body carries the new one rather than the first.
    auth: (cb) => cb({ token: AUTH_TOKEN || undefined, appearAs: wanted, body: useBodyStore.getState().body || undefined }),
    transports: ['websocket'],
    reconnection: true,
  });

  socket.on('world_state', ({ t, online }) => {
    if (t) offset = t - Date.now();
    if (typeof online === 'number') useWorldStore.getState().setOnline(online);
  });

  // Which of this member's artists the story is about (server-resolved: chosen or
  // first owned). Story systems read it; it is never a body or a spawn point.
  socket.on('me', (data) => {
    const got = data?.avatarId || null;
    if (wanted && String(got) !== String(wanted)) {
      console.warn(`[world] ⚠ asked to appear as avatar ${wanted} but the server made you ${got}. `
        + 'It could not honour the pick (not owned, or no row) and fell back to your first owned avatar with an image.');
    }
    useWorldStore.getState().setMyAvatarId(got);
  });

  socket.on('roster', (list) => { peers.clear(); (list || []).forEach((p) => peers.set(p.id, p)); pushPeers(); });
  socket.on('peer_enter', (p) => { peers.set(p.id, p); pushPeers(); });
  socket.on('peer_move', ({ id, pos, heading }) => {
    const p = peers.get(id);
    if (p) { p.pos = pos; p.heading = heading; pushPeers(); }
  });
  // Proximity chat: a peer said something → bubble over their figure + chat log.
  socket.on('peer_say', (m) => {
    if (!m?.id) return;
    const p = peers.get(m.id);
    if (p) { p.say = { text: m.text, ts: m.ts }; pushPeers(); }
    // `private`: said to you across a table, and to nobody else (the server routed it).
    useWorldStore.getState().pushChat({ id: m.id, handle: m.handle, text: m.text, ts: m.ts, priv: !!m.private });
  });

  socket.on('peer_body', ({ id, body } = {}) => {
    const p = peers.get(id);
    if (p) { p.body = body || null; pushPeers(); }
  });
  // YOUR BODY CHANGED (the picker, first visit or the HUD's Body button): the
  // room hears it now; anyone arriving later gets it with the roster.
  if (bodyUnsub) bodyUnsub();
  bodyUnsub = useBodyStore.subscribe((st, prev) => {
    if (st.body !== prev.body && socket?.connected) socket.emit('body', st.body || '');
  });

  socket.on('peer_leave', ({ id }) => {
    if (peers.delete(id)) pushPeers();
  });
  // SOMEBODY IS CALLING YOU. Addressed to the member, not to a room — the
  // server puts every signed-in socket in a `user:<id>` channel for exactly
  // this. A notification (and push) goes out alongside; this is the copy that
  // arrives while the caller is still holding the phone to their ear.
  socket.on('incoming_call', (payload = {}) => {
    if (!payload.code) return;
    console.info(`[world] incoming call from ${payload.from?.name || 'someone'} (${payload.code})`);
    useWorldStore.getState().ringVideo({
      code: payload.code,
      from: payload.from || null,
      asAvatarId: payload.asAvatarId || null,
      at: payload.at || Date.now(),
    });
  });

  // SOMEBODY SAVED YOU A SEAT. Same channel and same reasoning as the call
  // above — addressed to the member via `user:<id>`, arriving while the host is
  // still holding a chair, with the notification as the copy you find later.
  socket.on('listening_invite', (payload = {}) => {
    if (!payload.code) return;
    console.info(`[world] listening invite from ${payload.from?.name || 'someone'} (${payload.code}, ${payload.zone})`);
    const inv = {
      code: payload.code,
      roomId: payload.roomId || null,
      zone: payload.zone || null,
      from: payload.from || null,
      at: payload.at || Date.now(),
    };
    const st = useWorldStore.getState();
    st.setListeningInvite(inv);
    // AND INTO THE PHONE. The socket message alone only reached somebody who
    // happened to be standing in the right room with the dock open — an invite
    // has to survive not looking at it. The Calls app badges, keeps it, and
    // gives it a button.
    st.noteListeningInvite(inv);
    // Bring the phone up on the Calls screen: "it should appear in my phone (on
    // screen)". Deliberately NOT a full-screen ring like a video call — you are
    // being offered a seat, not summoned, and hijacking the viewport for it
    // would make every invite an interruption.
    try { st.setPhoneApp?.('calls'); st.setPhoneOpen?.(true); } catch { /* phone not mounted */ }
  });

  // The room changed: somebody sat down, a chair freed, the host put a record
  // on. Broadcast to the ZONE, so it reaches everyone standing in it whether or
  // not they have a seat — an intimate room is partly about being able to see
  // it filling up.
  socket.on('listening_state', (payload = {}) => {
    if (payload?.room) useWorldStore.getState().setListeningRoom(payload.room);
  });

  // ── THE LIVING CITY (city/) ─────────────────────────────────────────────
  // The whole city now and then (a new day, a meetup agreed, someone stopped to
  // talk), each line of a conversation as it is written, and what an artist
  // said back to a visitor — for the people standing near them.
  socket.on('city_state', (snap) => useCityStore.getState().setSnap(snap));
  socket.on('city_line', (line) => useCityStore.getState().addLine(line));
  // Paying member, or which conversation was this free visit's one (API CityLifeService.entitled).
  socket.on('city_allowance', (a) => useCityStore.setState({ allowance: a || null }));
  socket.on('city_said', (m) => useCityStore.getState().addSaid(m));
  // A venue host with one visitor comes down from the booth and talks to them
  // (API sockets/venueMingle, 2026-10-30): the mode and the lines, for VenueStage.
  bindVenueMingle(socket);

  // A shared room's chairs (see SHARED SEATS above): who holds which, for the
  // room you are standing in. Replaced whole — a café is a dozen chairs.
  socket.on('seat_state', ({ zone, seats, t } = {}) => {
    if (!zone || zone !== roomKey()) return;
    const me = socket?.id || null;
    const out = {};
    for (const [name, h] of Object.entries(seats || {})) {
      if (h) out[name] = { mine: !!me && h.id === me, until: toLocal(h.until, t), by: h.by || 'Someone', group: h.group || null };
    }
    useWorldStore.getState().setRoomSeats({ zone, seats: out });
    // A HOLD THE SERVER EXTENDED (a table of two runs longer: sharedSeatStore)
    // reaches the chair you are in, or SceneStaging would stand you up at the
    // two minutes you sat down with.
    const st = useWorldStore.getState();
    const mine = st.seated?.target ? out[st.seated.target] : null;
    if (mine?.mine && mine.until && mine.until !== st.seated.until) useWorldStore.setState({ seated: { ...st.seated, until: mine.until } });
  });

  // Every pad of the heliport, to everyone in the street (heli tours, above).
  socket.on('heli_state', ({ pads, t } = {}) => {
    useHeliTour.setState({ pads: heliPadsLocal(pads, t), offline: false, myId: socket?.id || null });
  });

  // …and a map from a socket that is gone is a list of chairs nobody holds.
  socket.on('disconnect', () => { peers.clear(); pushPeers(); useWorldStore.getState().setRoomSeats(null); });

  // Follow the local player's area: (re)announce `enter` whenever the room changes.
  let lastRoom = null;
  const syncRoom = () => {
    if (!socket || !socket.connected) return; // may be null after disconnect
    const room = roomKey();
    if (room === lastRoom) return;
    lastRoom = room;
    peers.clear(); pushPeers();      // leaving an area clears its roster
    socket.emit('enter', { room });
    // The last room's chairs go with it; a shared room's are asked for (after
    // `enter`, so the server already has you standing in it).
    useWorldStore.getState().setRoomSeats(null);
    if (ROOMS[room]?.sharedSeats) seatHello(room);
    // The street: what the heliport's pads are doing (an API without heli tours just doesn't answer).
    if (room === 'street') socket.emit('heli_hello');
  };
  socket.on('connect', () => {
    lastRoom = null; syncRoom();
    reclaimSeat();
    for (const fn of connectListeners) { try { fn(); } catch { /* one listener never stops the rest */ } }
  });
  // Cheap guards inside syncRoom / releaseOnStand mean subscribing to every
  // store change is fine. Keep the unsub so disconnect can drop it (else it
  // fires against a null socket).
  if (storeUnsub) storeUnsub();
  storeUnsub = useWorldStore.subscribe((s, prev) => { syncRoom(); releaseOnStand(s, prev); });

  // Clean, INSTANT leave on refresh/close/tab-hide (the common case) so our marker
  // doesn't linger for others. A hard crash/kill is caught by the server's ping
  // timeout instead. pagehide fires on iOS where beforeunload doesn't.
  window.addEventListener('pagehide', () => { try { socket?.emit('leave'); } catch { /* unloading */ } });

  return socket;
}

// Called from WalkControls/InteriorWalk once position emitting is wired. Throttled
// to ~8 Hz; the server relays as volatile (drops under congestion).
let lastMove = 0;
export function emitMove(pos, heading) {
  if (!socket || !socket.connected) return;
  const now = Date.now();
  if (now - lastMove < 120) return;
  lastMove = now;
  socket.emit('move', { pos, heading });
}

// Send a proximity chat line to peers in your area. Locally echoed into the log
// (you don't see your own bubble in first-person).
// `viaAgent`: a venue host's voice agent already has it and answers it (city/
// venueAgent), so the server keeps it as context and does not answer it again.
export function sendSay(text, { viaAgent = false } = {}) {
  const t = String(text || '').trim().slice(0, 200);
  if (!socket || !socket.connected || !t) return;
  socket.emit('say', { text: t, ...(viaAgent ? { viaAgent: true } : {}) });
  const mate = tableMate();
  useWorldStore.getState().pushChat({ id: 'me', handle: 'You', text: t, ts: Date.now(), me: true, priv: !!mate, to: mate?.by || null });
}

/**
 * Who you are sitting across from at a table, if anyone (2026-10-31: a table
 * of two talks privately — the SERVER routes it, sharedSeatStore.tableMates;
 * this only says so on screen): { by } or null. From the room's seat map.
 */
export function tableMate() {
  const st = useWorldStore.getState();
  const seats = st.roomSeats?.zone === st.interior ? st.roomSeats.seats : null;
  if (!seats) return null;
  const mine = Object.values(seats).find((h) => h.mine && h.until > Date.now());
  if (!mine?.group) return null;
  const other = Object.values(seats).find((h) => !h.mine && h.group === mine.group && h.until > Date.now());
  return other ? { by: other.by } : null;
}

// ── The city: listening, stopping someone to talk, what they said back ──────
/** Ask for the live city again (after a replay set it aside). */
export function cityHello() {
  if (socket?.connected) socket.emit('city_hello');
}
export function cityListen(conv, on) {
  if (socket?.connected && conv) socket.emit('city_listen', { conv, on: !!on });
}
/** Ask to talk to an artist. Resolves { ok, reason?, activity?, mustGoAt? }. */
export function cityEngage(avatarId) {
  return new Promise((resolve) => {
    if (!socket?.connected) { resolve({ ok: true, offline: true }); return; }
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve({ ok: true, offline: true }); } }, 2500);
    socket.emit('city_engage', { avatarId }, (r) => { if (done) return; done = true; clearTimeout(t); resolve(r || { ok: true }); });
  });
}
/**
 * Run `fn` each time the world socket (re)connects; returns the unsubscribe.
 * The city's hold on an artist is keyed to the socket and dropped with it, so
 * an open conversation asks again the moment the socket is back.
 */
export function onWorldConnect(fn) {
  connectListeners.add(fn);
  return () => connectListeners.delete(fn);
}
/** What the visitor is told when the city says no (cityEngage's reasons). */
export function busyLine(first, reason, activity) {
  if (reason === 'talking') return `${first} is talking with someone`;
  if (reason === 'leaving') return `${first} is about to head off`;
  if (reason === 'not_here') return `${first} is on the move`;
  return activity ? `${first} is busy · ${activity}` : `${first} is busy right now`;
}

/**
 * Are they about to walk (or drive) somewhere in the city? Their own timeline
 * says: a move starting within `withinMs` that ends at a place, not home.
 */
function headingOff(r, now, withinMs = 120000) {
  for (const s of r?.segs || []) {
    if (s.t1 <= now) continue;
    if (s.t0 > now + withinMs) return false;
    if (s.k === 'away') return false;
    if ((s.k === 'walk' || s.k === 'drive') && (s.to?.place || s.p)) return true;
  }
  return false;
}

const offeredAt = new Map();   // avatarId → when "Walk with" was last offered
export function cityRelease(avatarId) {
  if (socket?.connected && avatarId) socket.emit('city_release', { avatarId });
  // "…and then leave them with something, maybe an invitation to walk with me"
  // (Avva Digital). When a chat out in the city ends and they are about to set
  // off, the visitor is offered to walk with them: the follow mode of whatever
  // they do next. Only then: offered to someone staying put, it was a follow
  // that never went anywhere (2026-10-23). Once per goodbye (the dock closing
  // after the conversation ended releases again).
  try {
    const id = Number(avatarId);
    const r = useCityStore.getState().byId?.[id];
    const now = serverNow();
    if (r && headingOff(r, now) && !(now - (offeredAt.get(id) || 0) < 120000)) {
      offeredAt.set(id, now);
      useCutscene.getState().setOffer({ kind: 'artist', id, title: `Walk with ${String(r.name).split(' ')[0]}`, full: r.name, until: Date.now() + 90000 });
    }
  } catch { /* no city here */ }
}
// Out in the city, each conversation asks again (they stop for you again, and
// say whether they have to go by now), and its end lets them get on with their
// day: after the goodbye they set off, or carry on where they are.
export function cityTalk(avatarId, first) {
  return {
    engage: async () => {
      const r = await cityEngage(avatarId);
      return r?.ok ? r : { ...r, ok: false, message: busyLine(first, r?.reason, r?.activity) };
    },
    onEnded: () => cityRelease(avatarId),
  };
}

/**
 * The artist chose to stay with this visitor (their stay_with_them tool): the
 * schedule yields (API CityLifeService.stay). Resolves { ok, until, yielded }.
 */
export function cityStay(avatarId) {
  return new Promise((resolve) => {
    if (!socket?.connected || !avatarId) { resolve({ ok: false }); return; }
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve({ ok: false }); } }, 4000);
    socket.emit('city_stay', { avatarId }, (r) => { if (done) return; done = true; clearTimeout(t); resolve(r || { ok: false }); });
  });
}
/** The artist's reply to this visitor, so people standing near can see it. */
export function cityReply(avatarId, text) {
  if (socket?.connected && avatarId && text) socket.emit('city_reply', { avatarId, text: String(text).slice(0, 600) });
}
export function citySocketId() { return socket?.id || null; }

export function disconnectWorld() {
  if (storeUnsub) { storeUnsub(); storeUnsub = null; }
  if (bodyUnsub) { bodyUnsub(); bodyUnsub = null; }
  if (socket) { try { socket.disconnect(); } catch { /* noop */ } socket = null; }
}
